import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";

import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import { cls } from "@kikoff/utils/src/string";

import styles from "./tabs.module.scss";

type TabItem = { label: React.ReactNode } & (
  | { link: string }
  | { onClick(): void }
);

interface TabsProps {
  className?: string;
  variant: "underline" | "chip";
  items: TabItem[];
  activeIndex: number;
}

export default function Tabs({
  className,
  variant,
  items,
  activeIndex,
}: TabsProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [indicatorPos, setIndicatorPos] = useState({});

  useEffect(() => {
    if (variant === "underline") {
      const tab = ref.current.getElementsByClassName(styles.tab)[
        activeIndex
      ] as HTMLElement;
      if (tab) {
        setIndicatorPos({ left: tab.offsetLeft, width: tab.clientWidth });
      }
    }
  }, [activeIndex, variant]);

  return (
    <div
      className={cls(className, "py-2", styles.tabs, styles[variant])}
      ref={ref}
    >
      {items.map((tab, i) => (
        <Tab active={activeIndex === i} tabItem={tab} key={i} />
      ))}

      {variant === "underline" && (
        <div className={styles.indicator} style={indicatorPos} />
      )}
    </div>
  );
}

interface TabProps {
  active: boolean;
  tabItem: TabItem;
}

function Tab({ active, tabItem }: TabProps) {
  const props = {
    className: cls(styles.tab, active && styles.active),
    style: {
      fontWeight: (active
        ? "bold"
        : "normal") as React.CSSProperties["fontWeight"],
    },
  };
  return "link" in tabItem ? (
    <Link href={tabItem.link}>
      <a {...props}>{tabItem.label}</a>
    </Link>
  ) : (
    <ContainerButton onClick={tabItem.onClick} {...props}>
      {tabItem.label}
    </ContainerButton>
  );
}
